import "../../styles/BulkTagMappingLandingPage.css";

/**
 * SelectCell component for individual row checkboxes.
 * Only shows checkbox if the row doesn't have a UD assigned.
 *
 * @param {Object} props - Component props
 * @param {Object} props.row - The row data object
 * @param {number} props.row.id - The unique identifier for the row
 * @param {number|null} props.row.udId - The Universal Descriptor ID (null if unassigned)
 * @param {Set<number>} props.selectedRows - Set of selected row IDs
 * @param {Function} props.onSelectionChange - Callback function called when selection changes, receives a new Set of selected row IDs
 */
const SelectCell = ({ row, selectedRows, onSelectionChange }) => {
  const hasUD = row.udId != null && row.udId !== 0;

  // Hide checkbox if UD is assigned - not eligible for Bulk Assignment.
  if (hasUD) {
    return null;
  }

  // Show checkbox if no UD is assigned - can be selected for Bulk Assignment.
  const isSelected = selectedRows.has(row.id);

  const handleToggle = (e) => {
    e.stopPropagation();
    const newSelected = new Set(selectedRows);
    if (isSelected) {
      newSelected.delete(row.id);
    } else {
      newSelected.add(row.id);
    }
    onSelectionChange(newSelected);
  };

  return (
    <div
      className='bulk-tag-mapping-checkbox-container'
      onClick={handleToggle}
      style={{ cursor: "pointer" }}>
      <input
        type='checkbox'
        checked={isSelected}
        onChange={handleToggle}
        onClick={(e) => e.stopPropagation()} // Prevent double-toggling
        className='bulk-tag-mapping-checkbox'
      />
    </div>
  );
};

/**
 * SelectHeaderCell component for the header "select all" checkbox.
 * Only shows if there are selectable rows (rows without UD).
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.sortedRows - Array of sorted row objects to check for selectability
 * @param {Set<number>} props.selectedRows - Set of selected row IDs
 * @param {Function} props.onSelectionChange - Callback function called when selection changes, receives a new Set of selected row IDs
 */
const SelectHeaderCell = ({ sortedRows, selectedRows, onSelectionChange }) => {
  // Only show header checkbox if there are any selectable rows.
  const selectableRows = sortedRows.filter(
    (row) => !(row.udId != null && row.udId !== 0)
  );
  if (selectableRows.length === 0) {
    return null;
  }

  const allSelectableSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedRows.has(row.id));

  const handleToggle = (e) => {
    e.stopPropagation();
    const newSelected = new Set(selectedRows);
    if (allSelectableSelected) {
      selectableRows.forEach((row) => newSelected.delete(row.id));
    } else {
      selectableRows.forEach((row) => newSelected.add(row.id));
    }
    onSelectionChange(newSelected);
  };

  return (
    <div
      className='bulk-tag-mapping-checkbox-container'
      onClick={handleToggle}
      style={{ cursor: "pointer" }}>
      <input
        type='checkbox'
        checked={allSelectableSelected}
        onChange={handleToggle}
        onClick={(e) => e.stopPropagation()} // Prevent double-toggling
        className='bulk-tag-mapping-checkbox'
      />
    </div>
  );
};

/**
 * Creates a custom select column configuration for the react-data-grid used in BulkTagMappingLandingPage.
 * This column conditionally shows checkboxes based on UD assignment.
 *
 * @param {Set} selectedRows - Set of selected row IDs
 * @param {Function} onSelectionChange - Callback when selection changes
 * @param {Array} sortedRows - Array of sorted rows (for header checkbox logic)
 * @returns {Object} Column definition object for react-data-grid
 */
export const createBulkTagMappingSelectColumn = (
  selectedRows,
  onSelectionChange,
  sortedRows
) => {
  return {
    key: "select",
    name: "",
    width: 35,
    resizable: false,
    sortable: false,
    renderCell: ({ row }) => (
      <SelectCell
        row={row}
        selectedRows={selectedRows}
        onSelectionChange={onSelectionChange}
      />
    ),
    renderHeaderCell: () => (
      <SelectHeaderCell
        sortedRows={sortedRows}
        selectedRows={selectedRows}
        onSelectionChange={onSelectionChange}
      />
    ),
  };
};
